import type { SigninAnnouncementFormValues } from '@/state/useSigninPanel';
import type { AnnouncementStatus } from './announcement-form';
import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { SIGNIN_HISTORY_LIMIT } from 'definitions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSigninAnnouncementsAdmin } from '@/state/useSigninPanel';
import { formatAnnouncementDate } from '../../Authentication/chat/news-script';
import { Panel } from '../../Dashboard/ConsolePanel';
import { AnnouncementEditor } from './AnnouncementEditor';
import { announcementStatus, blankAnnouncement, liveAnnouncementIds, toFormValues } from './announcement-form';

// null is the list; 'new' is a blank form; a number is that row.
type Editing = null | 'new' | number;

const STATUS_LABEL: Record<AnnouncementStatus, string> = {
  live: 'Live',
  history: 'Published, not shown',
  draft: 'Draft',
};

const STATUS_CLASS: Record<AnnouncementStatus, string> = {
  live: 'text-ok border-ok-line bg-ok-bg',
  history: 'text-dim border-line bg-field',
  draft: 'text-dim border-line bg-field',
};

type StatusPillProps = { status: AnnouncementStatus };

const StatusPill = ({ status }: StatusPillProps) => (
  <span className={`micro-label rounded-lg border px-2 py-1 ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>
);

/**
 * Super Admin: what the signin screen's chat says. A history, not a slot: the
 * newest published announcements play on the login screen and older ones stay here,
 * so a launch can be republished or copied from later.
 */
export const SigninPanel = () => {
  const { announcements, isLoading, save, remove } = useSigninAnnouncementsAdmin();
  const [editing, setEditing] = useState<Editing>(null);
  const [isSaving, setIsSaving] = useState(false);

  const liveIds = liveAnnouncementIds(announcements);
  const editingRow = typeof editing === 'number' ? announcements.find((row) => row.id === editing) : undefined;

  const handleSave = async (values: SigninAnnouncementFormValues) => {
    setIsSaving(true);
    try {
      await save({ id: typeof editing === 'number' ? editing : null, values });
      toast.success('Signin announcement saved');
      setEditing(null);
    } catch (ex) {
      // The API says which rule failed (a headline missing, an unsafe link), and the
      // reason is the useful part. The form stays open with everything typed.
      const message = (ex as { message?: string })?.message;
      toast.error(message || 'Could not save the announcement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await remove(id);
      toast.success('Announcement deleted');
    } catch (ex) {
      toast.error((ex as { message?: string })?.message || 'Could not delete the announcement');
    }
  };

  if (isLoading) return <Skeleton className="bg-panel2 h-96 w-full rounded-panel" />;

  if (editing !== null && (editing === 'new' || editingRow)) {
    return (
      <div className="flex w-full max-w-3xl flex-col gap-[14px]">
        <AnnouncementEditor
          // Remounts on switching rows: the form copies its initial values once.
          key={editing}
          initial={editingRow ? toFormValues(editingRow) : blankAnnouncement()}
          isNew={editing === 'new'}
          isSaving={isSaving}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-[14px]">
      <Panel
        title="Signin announcements"
        description={`The signin screen plays the newest ${SIGNIN_HISTORY_LIMIT} published announcements, newest first. Older ones stay here as history.`}
        actions={
          <Button onClick={() => setEditing('new')} className="h-9 rounded-lg">
            <Plus className="mr-2 h-4 w-4" />
            New announcement
          </Button>
        }
        bodyClassName="flex flex-col p-0"
      >
        {announcements.length === 0 && (
          <p className="supporting m-0 p-4">
            Nothing yet, so the signin screen shows its standing product copy. Add an announcement to replace it.
          </p>
        )}

        {announcements.map((row) => (
          <div key={row.id} className="border-line flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="log-line text-dim">{formatAnnouncementDate(row.publishedAt)}</span>
              <span className="text-foreground truncate text-lede font-medium">{row.headline || 'Untitled draft'}</span>
            </div>
            <StatusPill status={announcementStatus({ row, liveIds })} />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg"
              aria-label={`Edit ${row.headline || 'untitled draft'}`}
              onClick={() => setEditing(row.id)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${row.headline || 'untitled draft'}`}
                  className="text-dim hover:bg-danger-bg hover:text-danger h-8 w-8 rounded-lg"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete announcement</AlertDialogTitle>
                  <AlertDialogDescription>
                    {row.active
                      ? 'This one is published, so it will disappear from the signin screen. This action cannot be undone.'
                      : 'This action cannot be undone.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-line rounded-lg bg-transparent">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleRemove(row.id)}
                    className="bg-danger-bg border-danger-line text-danger hover:bg-danger-bg rounded-lg border"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
      </Panel>
    </div>
  );
};
